// @ts-nocheck
import isEmpty from 'lodash/isEmpty';

/**
 * Note: This custom implementation is used instead of lodash's `isEmpty` function
 *       due to a known issue with lodash's handling of numbers, false, true values.
 *       Refer to https://github.com/lodash/lodash/issues/496 for more information.
 *
 * The function checks if a value is considered empty, handling various data types.
 * @param value - The value parameter is a variable that can hold any data type (undefined, null,
 * object, string, number, boolean, etc.) and is used to check if it is empty or not.
 * @returns The function `isValueEmpty` takes in a parameter `value` and returns a boolean value. It
 * returns `true` if the value is `undefined`, `null`, an empty object, or a string with only
 * whitespace characters. Otherwise, it returns `false`.
 */
export const isValueEmpty = (value) => {
  switch (true) {
    case value === undefined:
    case value === null:
      return true;
    case typeof value === 'object':
      return !Object.keys(value).length;
    case typeof value === 'string':
      return !value.trim().length;
    default:
      return false;
  }
};

/**
 * Checks if a value is non-empty but cannot be parsed into a valid number.
 * Returns true if the value is not empty/null/undefined and is NaN when cast to Number.
 *
 * @param {*} val - Value to check
 * @returns {boolean} True if the value is an invalid number
 */
export const isInvalidNumber = (val) =>
  val !== '' && val !== null && val !== undefined && Number.isNaN(Number(val));

/**
 * Normalizes paragraph values to ensure they always conform to { title, description } object shape.
 * Handles objects, stringified JSON, plain strings, numbers, and null/empty values.
 *
 * @param {*} val - Incoming paragraph value
 * @returns {{ title: string, description: string }} Normalized paragraph object
 */
export const normalizeParagraphValue = (val) => {
  if (!val) return { title: '', description: '' };
  if (typeof val === 'object' && !Array.isArray(val)) {
    return {
      title: val.title ?? '',
      description: val.description ?? '',
    };
  }
  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return {
          title: parsed.title ?? '',
          description: parsed.description ?? '',
        };
      }
    } catch {
      // not JSON
    }
    return { title: val, description: '' };
  }
  if (typeof val === 'number') {
    return { title: String(val), description: '' };
  }
  return { title: '', description: '' };
};

export const isJsonString = (str) => {
  if (isEmpty(str)) {
    return true;
  }
  try {
    JSON.parse(str);
  } catch {
    return false;
  }
  return true;
};

export function isBase64(base64String) {
  try {
    const decodedData = atob(base64String);
    return ['\x89PNG', 'GIF', 'JFIF', 'WEBP', 'RIFF'].some((header) =>
      decodedData.startsWith(header)
    );
  } catch {
    return false;
  }
}

/**
 * Rounds a given number to a specified number of decimal places.
 *
 * @param {number} value - The number to be rounded.
 * @param {number} [roundOfNumber=3] - The number of decimal places to round to. Defaults to 3 if not specified.
 * @returns {number} The rounded number.
 *
 * @example
 * roundToDecimals(3.1415926535);
 * // returns 3.142
 *
 * @example
 * roundToDecimals(3.1415926535, 2);
 * // returns 3.14
 *
 * @example
 * roundToDecimals(3.1, 5);
 * // returns 3.1
 */
export function roundToDecimals(value, roundOfNumber = 3) {
  if (typeof value === 'number') {
    return parseFloat(value.toFixed(roundOfNumber));
  }
  return value;
}

export const truncate = (text, maxLength) => {
  return text?.length > maxLength ? text?.slice(0, maxLength) + '...' : text;
};

/**
 * Create dynamic template strings
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Template_literals#tagged_templates_and_escape_sequences}
 * @returns {string} formatted string
 * @example
 * const t2Closure = stringFromTemplate`${0} ${"foo"}!`;
 * // const t2Closure = stringFromTemplate([""," ","!"],0,"foo");
 * t2Closure("Hello", { foo: "World" }); // "Hello World!"
 */
export function stringFromTemplate(strings, ...keys) {
  return (...values) => {
    const dict = values[values.length - 1] || {};
    const result = [strings[0]];
    keys.forEach((key, i) => {
      const value = Number.isInteger(key) ? values[key] : dict[key];
      result.push(value, strings[i + 1]);
    });
    return result.join('');
  };
}
